import { computed } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import useUtilities from '~/composable/useUtilities'
import { useAppliedDiscountCodeStore } from '~/stores/voucher/useAppliedDiscountCodeStore'
import { useCartStore } from '~/stores/useCartStore'
import { isPercentageVoucherDiscount } from '~/composable/voucher/resolveDevicePriceForCart'

/**
 * Composable for voucher price adjustments
 * Handles all voucher-related price calculations and logic
 * Gracefully handles if voucher feature stores are not available
 */
export const useVoucherPriceAdjustment = (productDetails) => {
  const { formatPrice, formatPriceWithDecimals } = useUtilities()

  // Initialize voucher stores if available
  let totalDiscountCodeDiscount, hasAppliedDiscountCode, appliedDiscountCodes, getCart
  let isVoucherFeatureAvailable = false

  try {
    const appliedDiscountCodeStore = useAppliedDiscountCodeStore()
    const cartStore = useCartStore()
    const discountCodeStoreRefs = storeToRefs(appliedDiscountCodeStore)
    totalDiscountCodeDiscount = discountCodeStoreRefs.totalDiscountCodeDiscount
    hasAppliedDiscountCode = discountCodeStoreRefs.hasAppliedDiscountCode
    appliedDiscountCodes = discountCodeStoreRefs.appliedDiscountCodes
    const cartStoreRefs = storeToRefs(cartStore)
    getCart = cartStoreRefs.getCart
    isVoucherFeatureAvailable = true
  } catch (e) {
    // Voucher feature stores not available - composable will work without voucher functionality
    isVoucherFeatureAvailable = false
  }

  // Helper functions for cart/product checking
  const cartContainsCurrentProduct = (cart, currentSku) => {
    if (!cart?.cartItemGroupList || !currentSku) {
      return false
    }
    return cart.cartItemGroupList.some(group =>
      group.cartItems?.some(item =>
        item.cartProduct?.sku === currentSku
      )
    )
  }

  const cartHasDeviceOrAccessory = (cart) => {
    if (!cart?.cartItemGroupList) {
      return false
    }
    return cart.cartItemGroupList.some(group =>
      group.cartItems?.some(item =>
        item.cartProduct?.productTypeKey === 'device' ||
        item.cartProduct?.productTypeKey === 'accessory'
      )
    )
  }

  const isCurrentProductDeviceOrAccessory = computed(() => {
    const currentProductType = productDetails?.value?.chosenVariant?.productTypeKey
    return currentProductType === 'device' || currentProductType === 'accessory'
  })

  const isCartEmpty = (cart) => {
    return !cart?.cartItemGroupList?.length
  }

  const shouldShowDiscountWhenCartHasItems = (cart, currentSku) => {
    if (cartContainsCurrentProduct(cart, currentSku)) {
      return true
    }
    return isCurrentProductDeviceOrAccessory.value && cartHasDeviceOrAccessory(cart)
  }

  const shouldShowDiscountForCartState = (cart, currentSku) => {
    if (!cart || !currentSku) {
      return true
    }
    if (isCartEmpty(cart)) {
      return true
    }
    return shouldShowDiscountWhenCartHasItems(cart, currentSku)
  }

  // Check if discount code applies to current product
  const discountCodeAppliesToCurrentProduct = computed(() => {
    if (!isVoucherFeatureAvailable || !hasAppliedDiscountCode?.value) {
      return false
    }
    const cart = getCart?.value
    const currentSku = productDetails?.value?.chosenVariantSku
    return shouldShowDiscountForCartState(cart, currentSku)
  })

  // Discount code amount (only if applies to current product)
  const discountCodeAmount = computed(() => {
    if (!isVoucherFeatureAvailable || !discountCodeAppliesToCurrentProduct.value) {
      return 0
    }
    return totalDiscountCodeDiscount?.value || 0
  })

  // Show discount code flag
  const showDiscountCode = computed(() => {
    if (!isVoucherFeatureAvailable || !hasAppliedDiscountCode?.value) {
      return false
    }
    return discountCodeAppliesToCurrentProduct.value
  })

  // Helper functions for getting price from cart
  const getAllCartItems = (cart) => {
    if (!Array.isArray(cart?.cartItemGroupList)) {
      return []
    }
    return cart.cartItemGroupList.flatMap(group => group.cartItems || [])
  }

  const isDeviceOrAccessoryItem = (item) => {
    return item?.cartProduct?.productTypeKey === 'device' ||
      item?.cartProduct?.productTypeKey === 'accessory'
  }

  const findDeviceItem = (cart, currentSku) => {
    const cartItems = getAllCartItems(cart)
    if (cartItems.length === 0) {
      return null
    }
    if (currentSku) {
      const matchedItem = cartItems.find(item =>
        isDeviceOrAccessoryItem(item) && item.cartProduct?.sku === currentSku
      )
      if (matchedItem) {
        return matchedItem
      }
    }
    return cartItems.find(item => isDeviceOrAccessoryItem(item)) || null
  }

  const getDeviceItemPrice = (deviceItem) => {
    return deviceItem?.cartProduct?.selectedPrice?.lineItemPrice || null
  }

  // Get actual price from cart if available
  const getActualPriceFromCart = () => {
    if (!isVoucherFeatureAvailable || !getCart) {
      return null
    }
    const cart = getCart.value
    if (!cart) {
      return null
    }
    const currentSku = productDetails?.value?.chosenVariantSku
    const deviceItem = findDeviceItem(cart, currentSku)
    return getDeviceItemPrice(deviceItem)
  }

  // Helper function to calculate adjusted price with voucher discount
  const calculateAdjustedPrice = (basePrice) => {
    if (!isVoucherFeatureAvailable) {
      return basePrice
    }
    return Math.max(0, basePrice - discountCodeAmount.value)
  }

  // Helper function to get price from cart if voucher applies
  const getPriceFromCartIfVoucherApplies = () => {
    if (!isVoucherFeatureAvailable || !discountCodeAppliesToCurrentProduct.value) {
      return null
    }
    return getActualPriceFromCart()
  }

  const usesPercentageVoucherDiscount = computed(() => {
    if (!hasAppliedDiscountCode?.value) {
      return false
    }
    if (isPercentageVoucherDiscount(getCart?.value)) {
      return true
    }
    return isPercentageVoucherDiscount(appliedDiscountCodes?.value)
  })

  const formatDisplayPrice = (nr) => {
    if (showDiscountCode.value && usesPercentageVoucherDiscount.value) {
      return formatPriceWithDecimals(nr)
    }
    return formatPrice(nr)
  }

  return {
    // Feature availability
    isVoucherFeatureAvailable,

    // Discount code info
    discountCodeAmount,
    showDiscountCode,
    usesPercentageVoucherDiscount,
    discountCodeAppliesToCurrentProduct,

    // Price adjustment functions
    calculateAdjustedPrice,
    getPriceFromCartIfVoucherApplies,
    getActualPriceFromCart,
    formatDisplayPrice
  }
}
